import { Card } from '../../../components/Card/Card';
import { WAS_BACK_END_API_URL, URL_VERSION } from '../../../constants';
import { fetchAuthenticated } from '../../../controllers';
import { useCallback, useEffect, useState } from 'react';
import './PatientInfusion.scss';
import { Header } from '../../../components/common/table/Header/Header';
import { HeaderCell } from '../../../components/common/table/HeaderCell/HeaderCell';
import { FilterButton } from '../../../components/common/buttons/iconButtons/FilterButton';
import { InfusionHistoryRow } from './InfusionHistoryRow/InfusionHistoryRow';
import { PatientRecord, Section } from '../../../shared/types/types';
import { LoadingSpinner, mediumSpinner } from '../../../components/common/loader/LoadingSpinner';
import { PatientBloodSamples } from './PatientBloodSamples/PatientBloodSamples';
import { InfusionNotes } from './InfusionNotes/InfusionNotes';
import { decryptObject } from '../../../components/Encryption/obfuscationHandler';

interface PatientInfusionProps {
  patientData?: PatientRecord;
  section: Section;
  token: string | null;
}

export interface InfusionSession {
  _id: string;
  body: {
    beg_date_time_utc: string;
    end_date_time_utc: string;
    status: string;
  };
  header: {
    item_id: string;
    patient_id: string;
    timestamp_local: string;
    timestamp_utc: string;
    version: number;
    user: {
      id: string;
      name: string;
    };
  };
}

export interface InfusionSessionsServiceResult {
  status: string;
  total: number;
  items: Array<InfusionSession>;
  errors: string[];
}

export interface InfusionSessionsAjaxResult {
  result: InfusionSessionsServiceResult;
}

export const PatientInfusion = ({ patientData, section, token }: PatientInfusionProps) => {
  const [infusionSessions, setInfusionSessions] = useState<InfusionSession[] | null>(null);

  const [isInfusionHistoryLoading, setInfusionHistoryIsLoading] = useState<boolean>();
  const [apiData, setApiData] = useState<InfusionSessionsAjaxResult>();
  const [serverError, setServerError] = useState();

  const getPatientInfusionData = useCallback(async () => {
    const config: RequestInit = {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    };

    if (!patientData) return;
    setInfusionHistoryIsLoading(true);
    const url = WAS_BACK_END_API_URL + `${URL_VERSION}/patients/${patientData?._id}/infusions`;

    const response = await fetchAuthenticated<InfusionSessionsAjaxResult>(url, config);
    setInfusionHistoryIsLoading(false);

    if (response?.ok) {
      setApiData(response.parsedBody);
    }
  }, [token, patientData]);

  useEffect(() => {
    if (!patientData) return;
    try {
      getPatientInfusionData();
    } catch (error: any) {
      setServerError(error);
    }
  }, [getPatientInfusionData, patientData]);

  const [expanded, setExpanded] = useState(true);

  const handleOnExpand = () => {
    setExpanded(!expanded);
  };

  useEffect(() => {
    async function decryptData() {
      if (apiData?.result?.items) {
        try {
          await decryptObject(apiData.result, token, null);
          setInfusionSessions(apiData?.result.items);
        } catch {
          setInfusionSessions(null);
        }
      }
    }
    decryptData();
  }, [apiData, token]);

  const body = expanded ? (
    <div className="patient-infusion-content-container">
      <div className="title">infusion history</div>

      <div className={'history-table-container'}>
        <table className="table-container">
          <Header>
            <HeaderCell>
              <div>infusion status</div>
              <FilterButton onClick={() => {}} width={24} height={24} />
            </HeaderCell>
            <HeaderCell>
              <div className="time-format-cell">
                <div>infusion date</div>
                <div style={{ fontSize: '12px', color: '#888', marginTop: '4px', textAlign: 'center', textTransform: 'none' }}>dd:MMM:YYYY</div>
              </div>
              <FilterButton onClick={() => {}} width={24} height={24} />
            </HeaderCell>
            <HeaderCell>
              <div className="time-format-cell">
                <div>start time</div>
                <div style={{ fontSize: '12px', color: '#888', marginTop: '4px', textAlign: 'center', textTransform: 'none' }}>HH:mm:ss</div>
              </div>
              <FilterButton onClick={() => {}} width={24} height={24} />
            </HeaderCell>
            <HeaderCell>
              <div className="time-format-cell">
                <div>end time</div>
                <div style={{ fontSize: '12px', color: '#888', marginTop: '4px', textAlign: 'center', textTransform: 'none' }}>HH:mm:ss</div>
              </div>
              <FilterButton onClick={() => {}} width={24} height={24} />
            </HeaderCell>
            <HeaderCell>
              <div>duration</div>
              <FilterButton onClick={() => {}} width={24} height={24} />
            </HeaderCell>
            <HeaderCell>
              <div>last updated</div>
              <FilterButton onClick={() => {}} width={24} height={24} />
            </HeaderCell>
          </Header>
          <tbody className="body-container">
            {infusionSessions &&
              infusionSessions.length > 0 &&
              infusionSessions.map((session, index) => <InfusionHistoryRow session={session} index={index} key={session._id} />)}
          </tbody>
        </table>
      </div>
      <div className="loader-container">
        {isInfusionHistoryLoading && <LoadingSpinner width={mediumSpinner} height={mediumSpinner} />}
        {serverError && <p>Error:</p>}
      </div>
      <div className="title">Blood Samples</div>
      <PatientBloodSamples patientId={patientData?._id} token={token} />

      <div className="title">Infusion Notes</div>
      <InfusionNotes patientId={patientData?._id} token={token} />
    </div>
  ) : (
    <></>
  );

  return (
    <div className="patient-infusion-container" data-testid="patient-infusion-card">
      <Card title={'INFUSION ACTIVITIES'} body={body} expandable expanded={expanded} onExpand={handleOnExpand} />
    </div>
  );
};
